import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  Clip, Source, clipLength, clipStart, formatTime, locate, timelineLength, uid,
} from '../lib/types'
import { Icon } from './Icon'

interface Props {
  sources: Source[]
  clips: Clip[]
  onClips(next: Clip[]): void
  onRecordMore(): void
  onPublish(): void
}

const MIN_CLIP = 0.25 // seconds — below this a clip is impossible to grab
const PAD = 0.04 // share of the timeline left empty on each side, so edges never sit flush

interface View { start: number; end: number }

/** The stretch of time the timeline bar shows, with breathing room either side. */
const fitView = (length: number): View => {
  const pad = Math.max(0.05, length * PAD)
  return { start: -pad, end: length + pad }
}

export function Editor({ sources, clips, onClips, onRecordMore, onPublish }: Props) {
  const [playhead, setPlayhead] = useState(0)
  const [playing, setPlaying] = useState(false)
  const [selected, setSelected] = useState<string | null>(null)
  /**
   * While a trim is in progress the timeline keeps the view it had when the
   * drag began, so the edge stays under the pointer instead of the bar
   * re-fitting on every move. It only widens if the clip is pulled past the
   * side margin, so you can keep extending.
   */
  const [drag, setDrag] = useState<{ clipId: string; edge: 'in' | 'out'; view: View; startIn: number } | null>(null)
  // Briefly true after a trim, so the bar eases into its new fit rather than jumping.
  const [settling, setSettling] = useState(false)
  const settleTimer = useRef(0)
  useEffect(() => () => clearTimeout(settleTimer.current), [])
  const videosRef = useRef<Record<string, HTMLVideoElement | null>>({})
  const rafRef = useRef(0)

  const total = useMemo(() => timelineLength(clips), [clips])
  const dragIdx = drag ? clips.findIndex((c) => c.id === drag.clipId) : -1
  // How far a start edge has moved. The dragged clip (and everything after
  // it) is drawn shifted by this much so its left edge follows the pointer;
  // when the start is pulled earlier, the clips before it shift too so
  // nothing overlaps.
  const lead = drag?.edge === 'in' && dragIdx >= 0 ? clips[dragIdx].in - drag.startIn : 0
  const shiftOf = (i: number) => (i < dragIdx ? Math.min(0, lead) : lead)

  let view = fitView(total)
  if (drag) {
    const pad = -drag.view.start
    view = {
      start: Math.min(drag.view.start, Math.min(0, lead) - pad),
      end: Math.max(drag.view.end, total + lead + pad),
    }
  }
  const span = Math.max(0.01, view.end - view.start)
  const toPct = (t: number) => ((t - view.start) / span) * 100
  const spanRef = useRef(span)
  spanRef.current = span

  const here = useMemo(() => locate(clips, playhead), [clips, playhead])
  const sourceById = useMemo(
    () => Object.fromEntries(sources.map((s) => [s.id, s])),
    [sources],
  )

  const activeVideo = () => (here ? videosRef.current[here.clip.sourceId] ?? null : null)

  /** Keep every off-screen take paused and parked; only the active one plays. */
  const syncVideos = useCallback(
    (seek: boolean) => {
      if (!here) return
      for (const [sid, v] of Object.entries(videosRef.current)) {
        if (!v) continue
        if (sid !== here.clip.sourceId && !v.paused) v.pause()
      }
      const v = activeVideo()
      if (!v) return
      const want = here.clip.in + here.local
      if (seek && Math.abs(v.currentTime - want) > 0.16) v.currentTime = want
    },
    [here],
  )

  useEffect(() => { syncVideos(true) }, [playhead, syncVideos])

  // Playback loop: follow the active <video>'s clock and hop to the next clip
  // when we run past this clip's out-point.
  useEffect(() => {
    if (!playing) {
      cancelAnimationFrame(rafRef.current)
      Object.values(videosRef.current).forEach((v) => v?.pause())
      return
    }
    const v = activeVideo()
    void v?.play().catch(() => setPlaying(false))

    const tick = () => {
      rafRef.current = requestAnimationFrame(tick)
      const cur = activeVideo()
      const spot = locate(clips, playheadRef.current)
      if (!cur || !spot) return
      const localNow = cur.currentTime - spot.clip.in
      const next = spot.offset + localNow
      if (localNow >= clipLength(spot.clip) - 0.03) {
        const boundary = clipStart(clips, spot.index) + clipLength(spot.clip)
        if (boundary >= total - 0.05) { setPlaying(false); setPlayhead(total) }
        else setPlayhead(boundary + 0.01)
        return
      }
      setPlayhead(Math.min(total, Math.max(0, next)))
    }
    rafRef.current = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(rafRef.current)
  }, [playing, clips, total, here?.clip.id])

  // A ref mirror of the playhead so the rAF loop reads a fresh value.
  const playheadRef = useRef(playhead)
  useEffect(() => { playheadRef.current = playhead }, [playhead])

  const togglePlay = () => {
    if (playhead >= total - 0.05) setPlayhead(0)
    setPlaying((p) => !p)
  }

  // ------------------------------------------------------------ edit actions

  /** Snip: cut the current clip in two at the playhead. */
  const split = () => {
    if (!here) return
    const { clip, local, index } = here
    const at = clip.in + local
    if (local < MIN_CLIP || clipLength(clip) - local < MIN_CLIP) return
    const left: Clip = { ...clip, out: at }
    const right: Clip = { id: uid(), sourceId: clip.sourceId, in: at, out: clip.out }
    const next = [...clips]
    next.splice(index, 1, left, right)
    onClips(next)
    setSelected(right.id)
  }

  const removeClip = (id: string) => {
    if (clips.length <= 1) return
    const next = clips.filter((c) => c.id !== id)
    onClips(next)
    setSelected(null)
    setPlayhead((p) => Math.min(p, timelineLength(next)))
  }

  /**
   * Click (or drag) anywhere on the timeline to move the playhead there.
   * Trim handles stop propagation, so grabbing an edge still trims rather
   * than seeking.
   */
  const seekTo = (clientX: number, track: HTMLElement) => {
    const r = track.getBoundingClientRect()
    const fraction = (clientX - r.left) / Math.max(1, r.width)
    setPlayhead(clamp(view.start + fraction * span, 0, total))
  }

  const scrubTimeline = (e: React.PointerEvent) => {
    const track = e.currentTarget as HTMLElement
    setPlaying(false)
    seekTo(e.clientX, track)
    const onMove = (ev: PointerEvent) => seekTo(ev.clientX, track)
    const onUp = () => {
      window.removeEventListener('pointermove', onMove)
      window.removeEventListener('pointerup', onUp)
    }
    window.addEventListener('pointermove', onMove)
    window.addEventListener('pointerup', onUp)
  }

  /** Drag a clip edge to trim it. */
  const trim = (clipId: string, edge: 'in' | 'out', e: React.PointerEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setPlaying(false)
    setSelected(clipId)
    const track = (e.currentTarget as HTMLElement).closest('.tl-track') as HTMLElement
    const rect = track.getBoundingClientRect()
    const idx = clips.findIndex((c) => c.id === clipId)
    const original = clips[idx]
    const src = sourceById[original.sourceId]
    let lastX = e.clientX
    let delta = 0
    setDrag({ clipId, edge, view, startIn: original.in })

    const onMove = (ev: PointerEvent) => {
      // Measured step by step against the bar's current scale, so the edge
      // keeps tracking the pointer even after the view has widened.
      delta += ((ev.clientX - lastX) * spanRef.current) / Math.max(1, rect.width)
      lastX = ev.clientX
      const next = [...clips]
      if (edge === 'in') {
        const lo = 0
        const hi = original.out - MIN_CLIP
        next[idx] = { ...original, in: clamp(original.in + delta, lo, hi) }
      } else {
        const lo = original.in + MIN_CLIP
        const hi = src ? src.duration : original.out
        next[idx] = { ...original, out: clamp(original.out + delta, lo, hi) }
      }
      onClips(next)
      // Park the playhead on the edge being trimmed so the preview shows the
      // exact frame the cut lands on.
      const start = clipStart(next, idx)
      setPlayhead(edge === 'in' ? start : Math.max(start, start + clipLength(next[idx]) - 0.02))
    }
    const onUp = () => {
      window.removeEventListener('pointermove', onMove)
      window.removeEventListener('pointerup', onUp)
      setDrag(null)
      setSettling(true)
      clearTimeout(settleTimer.current)
      settleTimer.current = window.setTimeout(() => setSettling(false), 260)
    }
    window.addEventListener('pointermove', onMove)
    window.addEventListener('pointerup', onUp)
  }

  // Keyboard: space to play, S to snip, Delete to remove selection.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const tag = (e.target as HTMLElement)?.tagName
      if (tag === 'INPUT' || tag === 'TEXTAREA') return
      if (e.code === 'Space') { e.preventDefault(); togglePlay() }
      else if (e.key === 's' || e.key === 'S') { e.preventDefault(); split() }
      else if ((e.key === 'Backspace' || e.key === 'Delete') && selected) {
        e.preventDefault(); removeClip(selected)
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  })

  return (
    <>
      <div className="card">
        <div className="preview">
          {sources.map((s) => (
            <video
              key={s.id}
              ref={(el) => { videosRef.current[s.id] = el }}
              src={s.url}
              className={here?.clip.sourceId === s.id ? 'on' : ''}
              playsInline
              preload="auto"
            />
          ))}
        </div>

        <div style={{ height: 14 }} />

        <div className="wrap-row">
          <button className="btn" onClick={togglePlay}>
            <Icon name={playing ? 'pause' : 'play'} />
            {playing ? 'Pause' : 'Play'}
          </button>
          <span className="time muted small" style={{ fontVariantNumeric: 'tabular-nums' }}>
            {formatTime(playhead, true)} / {formatTime(total, true)}
          </span>
          <div className="spacer" style={{ flex: 1 }} />
          <button className="btn" onClick={split} title="Snip at the playhead (S)">
            <Icon name="scissors" /> Snip here
          </button>
          <button
            className="btn"
            onClick={() => selected && removeClip(selected)}
            disabled={!selected || clips.length <= 1}
            title="Delete the selected piece (Delete)"
          >
            <Icon name="trash" /> Delete piece
          </button>
        </div>

        <div style={{ height: 12 }} />

        <input
          className="scrub"
          type="range"
          min={0}
          max={Math.max(0.01, total)}
          step={0.01}
          value={playhead}
          onChange={(e) => { setPlaying(false); setPlayhead(Number(e.target.value)) }}
          aria-label="Scrub through the recording"
        />

        <div className="timeline">
          <div
            className={`tl-track ${settling ? 'settling' : ''}`}
            onPointerDown={scrubTimeline}
          >
            {clips.map((c, i) => {
              const left = toPct(clipStart(clips, i) + shiftOf(i))
              const width = (clipLength(c) / span) * 100
              return (
                <div
                  key={c.id}
                  className={`tl-clip ${selected === c.id ? 'sel' : ''}`}
                  style={{ left: `${left}%`, width: `calc(${width}% - 2px)` }}
                  onPointerDown={() => setSelected(c.id)}
                  title="Click to move the playhead here. Drag the edges to trim."
                >
                  <span className="handle l" onPointerDown={(e) => trim(c.id, 'in', e)} />
                  <span className="label">{formatTime(clipLength(c), true)}</span>
                  <span className="handle r" onPointerDown={(e) => trim(c.id, 'out', e)} />
                </div>
              )
            })}
            <div
              className="playhead"
              style={{ left: `${toPct(playhead + lead)}%` }}
            />
          </div>
        </div>

        <div style={{ height: 10 }} />
        <p className="small muted" style={{ margin: 0 }}>
          <span className="kbd">Space</span> play · <span className="kbd">S</span> snip at playhead ·{' '}
          <span className="kbd">Delete</span> remove selected piece · click the timeline to move the
          playhead · drag a piece's edge to trim it.
          Nothing is destroyed — your original takes stay intact until you publish.
        </p>
      </div>

      <div className="card">
        <div className="wrap-row">
          <button className="btn" onClick={onRecordMore}>
            <Icon name="record" /> Record more
          </button>
          <div style={{ flex: 1 }} />
          <button className="btn primary big" onClick={onPublish} disabled={total < 0.5}>
            Upload to YouTube <Icon name="arrowRight" />
          </button>
        </div>
      </div>
    </>
  )
}

const clamp = (v: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, v))
